import type { TFunction } from 'i18next'
import type { ReceiptElement } from '@/features/orders/utils/receiptTypes'
import type { PosCashClosing, PosCashDay, PosCashInterim, PosStaffAccount } from './types'

/** One layout for the bon on screen, the printer and the CSV. */
export type CashReportLine =
  | { kind: 'title'; text: string; big?: boolean }
  | { kind: 'center' | 'heading' | 'note' | 'reason' | 'small'; text: string }
  | { kind: 'row' | 'total'; label: string; cents: number; indent?: boolean; value?: string; big?: boolean }
  | { kind: 'grid'; cells: string[] }
  | { kind: 'rule' }

interface Format { t: TFunction; language: string }
const when = (date: string, language: string) => new Date(date).toLocaleString(language, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
const rule: CashReportLine = { kind: 'rule' }
const out = (cents: number) => cents ? -cents : 0
function period(from: string, to: string, language: string) {
  const start = when(from, language), end = when(to, language), [day] = end.split(', ')
  return `${start} – ${start.startsWith(day) ? new Date(to).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }) : end}`
}

function salesLines(day: PosCashDay, { t }: Format, detailed: boolean): CashReportLine[] {
  const rate = (value: number | null) => value === null ? t('cash.noRate') : `${String(value)} %`
  return [
    { kind: 'heading', text: t('cash.salesHeading') },
    ...day.sales.flatMap((s): CashReportLine[] => detailed ? [
      { kind: 'row', label: `${rate(s.rate)} ${t('cash.net')}`, cents: s.netCents },
      { kind: 'row', label: t('cash.vat'), cents: s.vatCents, indent: true },
      { kind: 'row', label: t('cash.gross'), cents: s.grossCents, indent: true },
    ] : [{ kind: 'row', label: `${rate(s.rate)} ${t('cash.gross')}`, cents: s.grossCents }]),
    { kind: 'total', label: t('cash.salesTotal'), cents: day.salesGrossCents },
  ]
}
const tenderLines = (day: PosCashDay, cashCents: number, cardCents: number, { t }: Format): CashReportLine[] => [
  { kind: 'heading', text: t('cash.tendersHeading') },
  { kind: 'row', label: t('cash.tenderCash', { count: day.cashPayments }), cents: cashCents },
  { kind: 'row', label: t('cash.tenderCard', { count: day.cardPayments }), cents: cardCents },
  ...(day.voucherPaidCents ? [{ kind: 'row', label: t('cash.tenderVoucher', { count: day.voucherPayments ?? 0 }), cents: day.voucherPaidCents } as const] : []),
]

export function zReportLines(c: PosCashClosing, restaurant: string, copy: boolean, format: Format): CashReportLine[] {
  const { t, language } = format, day = c.day
  const reason = [c.count.differenceReason && t(`cash.differenceReasons.${c.count.differenceReason}`), c.count.notes].filter(Boolean).join(' · ')
  return [
    ...(copy ? [{ kind: 'title', text: t('cash.copy') } as const] : []),
    { kind: 'title', text: t('cash.reportZ') },
    { kind: 'center', text: `${t('cash.reportNumber', { number: c.closeNumber })} · ${t('cash.register')}` },
    { kind: 'center', text: restaurant },
    { kind: 'center', text: period(c.openedAt, c.closedAt, language) },
    rule,
    ...(day ? [...salesLines(day, format, true), rule, ...tenderLines(day, c.cashPaidCents, c.cardPaidCents, format), rule,
      { kind: 'heading', text: t('cash.adjustmentsHeading') } as const,
      { kind: 'row', label: t('cash.discounts', { count: day.discountCount }), cents: out(day.discountCents) } as const,
      { kind: 'row', label: t('cash.cashRefunds', { count: day.cashRefunds }), cents: out(c.cashRefundedCents) } as const,
      ...(day.cardRefunds ? [{ kind: 'row', label: t('cash.cardRefunds', { count: day.cardRefunds }), cents: out(c.cardRefundedCents) } as const] : []),
      { kind: 'row', label: t('cash.cancelled', { count: day.cancelledCount }), cents: day.cancelledCents } as const,
      { kind: 'row', label: `${t('cash.tips')}${day.tipsCount ? ` (${String(day.tipsCount)})` : ''}`, cents: day.tipsCents } as const,
      ...(day.vouchersSoldCents ? [{ kind: 'row', label: t('cash.vouchersSold', { count: day.vouchersSold ?? 0 }), cents: day.vouchersSoldCents } as const] : []), rule] : []),
    { kind: 'heading', text: t('cash.cashHeading') },
    { kind: 'row', label: t('cash.opening'), cents: c.openingCents },
    { kind: 'row', label: t('cash.cashPaid'), cents: c.cashPaidCents },
    ...(c.voucherSaleCashCents ? [{ kind: 'row', label: t('cash.vouchersSoldCash'), cents: c.voucherSaleCashCents } as const] : []),
    { kind: 'row', label: t('cash.cashRefunded'), cents: out(c.cashRefundedCents) },
    ...(day ? day.movements.map(m => ({ kind: 'row', label: `${t(`cash.kinds.${m.kind}`)} · ${t(`cash.reasons.${m.reason}`)} (${String(m.count)})`, cents: m.cents } as const))
      : [{ kind: 'row', label: t('cash.manualNet'), cents: c.manualNetCents } as const]),
    { kind: 'total', label: t('cash.expected').toUpperCase(), cents: c.count.expectedCents },
    { kind: 'row', label: t('cash.counted'), cents: c.count.countedCents },
    { kind: 'row', label: t('cash.difference'), cents: c.count.differenceCents },
    ...(reason ? [{ kind: 'reason', text: reason } as const] : []),
    { kind: 'row', label: t('cash.withdrawn'), cents: out(c.withdrawnCents) },
    { kind: 'total', label: t('cash.retained').toUpperCase(), cents: c.retainedCents },
    rule,
    { kind: 'note', text: `${t('cash.closedBy')}: ${c.closedBy}` },
    ...(c.closeNumber > 1 ? [{ kind: 'note', text: `${t('cash.previous')}: ${t('cash.reportNumber', { number: c.closeNumber - 1 })}` } as const] : []),
    rule,
    { kind: 'small', text: t('cash.reportScope') },
  ]
}
export function xReportLines(x: PosCashInterim, restaurant: string, format: Format): CashReportLine[] {
  const { t, language } = format
  return [
    { kind: 'title', text: t('cash.reportX') },
    { kind: 'center', text: `${when(x.generatedAt, language)} · ${t('cash.register')}` },
    { kind: 'center', text: restaurant },
    ...(x.lastCloseNumber > 0 ? [{ kind: 'center', text: t('cash.sinceClose', { number: x.lastCloseNumber }) } as const] : []),
    rule,
    ...salesLines(x.day, format, false), rule, ...tenderLines(x.day, x.cashPaidCents, x.cardPaidCents, format),
    { kind: 'row', label: t('cash.expected'), cents: x.expectedCents },
    rule,
    { kind: 'small', text: t('cash.interimScope') },
  ]
}
/** Every line of the overview per employee, on screen and on paper. */
export function staffRows(p: PosStaffAccount, t: Format['t']) {
  return [
    { label: t('staffSales.orders', { count: p.orders }), cents: p.cashCents + p.cardCents + p.voucherCents - p.soldCents },
    { label: t('staffSales.sold'), cents: p.soldCents },
    { label: t('staffSales.card'), cents: out(p.cardCents) },
    { label: t('staffSales.redeemed'), cents: out(p.voucherCents) },
    { label: t('staffSales.refunds'), cents: out(p.refundCents) },
  ].filter((row, index) => index === 0 || row.cents !== 0)
}
export function staffSalesLines(p: PosStaffAccount, restaurant: string, { t, language }: Format): CashReportLine[] {
  return [
    { kind: 'title', text: t('staffSales.title') },
    { kind: 'center', text: restaurant },
    { kind: 'center', text: when(new Date().toISOString(), language) },
    rule,
    { kind: 'heading', text: p.name },
    ...staffRows(p, t).map(row => ({ kind: 'row', ...row }) as const),
    { kind: 'total', label: t('staffSales.netCash'), cents: p.netCashCents },
    { kind: 'row', label: t('staffSales.tips'), cents: p.tipCents },
    rule,
    { kind: 'small', text: t('cash.reportScope') },
  ]
}
export const cashMoney = (cents: number, language: string) => new Intl.NumberFormat(language, { style: 'currency', currency: 'EUR' }).format(cents / 100)
export function cashReportElements(lines: CashReportLine[], language: string, maxChars = 32): ReceiptElement[] {
  return [...lines.map((line): ReceiptElement => {
    switch (line.kind) {
      case 'title': return { type: 'text', align: 'center', bold: true, tall: line.big, text: line.text }
      case 'grid': {
        // Monospaced on the printer: equal columns, the first left-aligned and the amounts right-aligned.
        const width = Math.floor(maxChars / line.cells.length)
        return { type: 'text', align: 'left', text: line.cells.map((cell, i) => i ? cell.padStart(width) : cell.padEnd(width)).join('') }
      }
      case 'center': case 'small': return { type: 'text', align: 'center', text: line.text }
      case 'heading': return { type: 'text', align: 'left', bold: true, text: line.text }
      case 'note': case 'reason': return { type: 'text', align: 'left', text: line.text }
      case 'rule': return { type: 'line', char: '-' }
      default: return { type: 'columns', left: line.label, right: line.value ?? cashMoney(line.cents, language), leftBold: line.kind === 'total', rightBold: line.kind === 'total', tall: line.big }
    }
  }), { type: 'feed', lines: 2 }]
}
export function cashClosingCsv(c: PosCashClosing, lines: CashReportLine[], language: string): string {
  // Neutralize spreadsheet formula prefixes, including operator/actor supplied notes.
  const cell = (value: string | number) => {
    const text = String(value), safe = /^[=+@\-\t\r]/.test(text) ? `'${text}` : text
    return `"${safe.replace(/"/g, '""')}"`
  }
  const amount = (cents: number) => new Intl.NumberFormat(language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)
  return '﻿' + [
    [c.openedAt, c.closedAt, c.closedBy],
    ...lines.flatMap(line => line.kind === 'rule' ? [] : line.kind === 'grid' ? [line.cells] : 'text' in line ? [[line.text]] : [[line.label, amount(line.cents)]]), [],
    ['id', 'sourceType', 'sourceId', 'cashDeltaCents', 'amountCents', 'kind', 'reason', 'notes', 'recordedBy', 'recordedAt', 'reversesId'],
    ...c.entries.map(e => [e.id, e.sourceType, e.sourceId, e.cashDeltaCents, e.amountCents, e.kind, e.reason, e.notes, e.recordedBy, e.recordedAt, e.reversesId ?? '']),
  ].map(row => row.map(cell).join(';')).join('\r\n')
}
